import { differenceInDays, parseISO, setYear, format } from "date-fns";
import { OCCASION_TYPES, type OccasionType } from "@/lib/occasion-types";

interface OccasionRowProps {
  occasionType: OccasionType;
  label?: string | null;
  date: string;
  isRecurring: boolean;
  onWriteMessage?: () => void;
  onTap?: () => void;
  showWriteLink?: boolean;
}

function getDaysUntilOccasion(dateStr: string, isRecurring: boolean): number | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = parseISO(dateStr);

  if (!isRecurring) {
    const target = new Date(d);
    target.setHours(0, 0, 0, 0);
    const diff = differenceInDays(target, today);
    return diff < 0 ? null : diff; // null = past one-time occasion
  }

  let next = setYear(d, today.getFullYear());
  next.setHours(0, 0, 0, 0);
  if (next < today) {
    next = setYear(d, today.getFullYear() + 1);
    next.setHours(0, 0, 0, 0);
  }
  return differenceInDays(next, today);
}

const OccasionRow = ({
  occasionType,
  label,
  date,
  isRecurring,
  onWriteMessage,
  onTap,
  showWriteLink = false,
}: OccasionRowProps) => {
  const config = OCCASION_TYPES[occasionType];
  const daysUntil = getDaysUntilOccasion(date, isRecurring);
  const isPast = daysUntil === null;
  const isWithin30 = daysUntil !== null && daysUntil <= 30;
  const displayLabel = occasionType === "special_date" && label ? label : config.label;
  const dateObj = parseISO(date);

  let countdownText: string;
  if (isPast) {
    countdownText = format(dateObj, "yyyy");
  } else {
    countdownText = format(dateObj, "MMM d");
  }

  return (
    <div
      className={`flex items-center justify-between py-2 ${onTap ? "cursor-pointer" : ""}`}
      onClick={onTap}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-base">{config.emoji}</span>
        <div className="min-w-0">
          <p className="text-sm text-foreground truncate">{displayLabel}</p>
          {showWriteLink && onWriteMessage && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onWriteMessage();
              }}
              className="text-xs font-medium text-accent hover:underline"
            >
              Write a message →
            </button>
          )}
        </div>
      </div>
      <span
        className={`text-sm font-medium whitespace-nowrap ml-3 ${
          isPast
            ? "text-muted-foreground"
            : isWithin30
            ? "text-accent"
            : "text-muted-foreground"
        }`}
      >
        {countdownText}
      </span>
    </div>
  );
};

export default OccasionRow;

export { getDaysUntilOccasion };
